import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FaFileExcel, FaFileCsv } from "react-icons/fa6";
import PageWrapper from "components/PageWrapper";
import ShowToast from "components/ShowToast";
import { useSession } from "hooks/useSession";
import { useAuth } from "hooks/useAuth";
import { fetchWrapper } from "utils/apiFetch";
import { exportDataToFile } from "utils/exportUtils";
import "./PayoutLedgerReport.css";

const BASE_URL = "https://pro.mokshiq.in";

// Format currency in Indian numbering format (e.g. ₹1,23,456.75)
const formatINR = (amount, showSign = false, direction = "") => {
  if (amount === null || amount === undefined || isNaN(amount)) return "₹0.00";
  const num = parseFloat(amount);
  const formatted = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(num));

  if (showSign) {
    if (direction === "credit" || num > 0) return `+${formatted}`;
    if (direction === "debit" || num < 0) return `-${formatted}`;
  }
  return formatted;
};

// Date helpers
const getTodayStr = () => new Date().toISOString().split("T")[0];

const getMonthStartStr = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split("T")[0];
};

const getLast7DaysStr = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().split("T")[0];
};

export default function PayoutLedgerReport() {
  const { role: sessionRole, userid: sessionUserId } = useSession();
  const { user } = useAuth();
  const role = sessionRole || user?.role || localStorage.getItem("role") || "API Partner";
  const currentUserId = sessionUserId || user?.id || localStorage.getItem("userid") || "";

  const roleLower = (role || "").trim().toLowerCase();
  const roleId = Number(localStorage.getItem("role_id") || user?.role_id || 0);
  const isAdmin =
    roleLower.includes("admin") ||
    roleLower === "white label" ||
    roleId === 1 ||
    roleId === 6;

  // ─────────────────────────────────────────────────────────────
  // Filter States
  // ─────────────────────────────────────────────────────────────
  const [walletType, setWalletType] = useState("all"); // "all" | "main_wallet" | "recharge_wallet"
  const [fromDate, setFromDate] = useState(getMonthStartStr());
  const [toDate, setToDate] = useState(getTodayStr());
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [targetUserId, setTargetUserId] = useState(isAdmin ? "" : String(currentUserId));

  // ─────────────────────────────────────────────────────────────
  // Data States
  // ─────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [isHardRefreshing, setIsHardRefreshing] = useState(false);
  const [errorState, setErrorState] = useState(null); // null | { message: string, is403: boolean }
  const [reportData, setReportData] = useState({
    user_id: "",
    user_name: "",
    user_phone: "",
    role_id: null,
    current_main_wallet: 0,
    current_recharge_wallet: 0,
    period: {
      from: "",
      to: "",
      opening_balance: 0,
      closing_balance: 0,
    },
    summary: {
      total_fund_in: 0,
      total_payout: 0,
      total_charge: 0,
      net_movement: 0,
      total_entries: 0,
      success_count: 0,
      pending_count: 0,
      failed_count: 0,
    },
    page: 1,
    page_size: 25,
    total: 0,
    total_pages: 1,
    data: [],
  });

  const [expandedRowId, setExpandedRowId] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(new Date().toLocaleTimeString());
  const [searchTerm, setSearchTerm] = useState("");

  const displayedRows = useMemo(() => {
    if (!searchTerm.trim()) return reportData.data;
    const term = searchTerm.toLowerCase();
    return reportData.data.filter((r) =>
      (r.beneficiary || "").toLowerCase().includes(term) ||
      (r.reference_no || "").toLowerCase().includes(term) ||
      (r.ack_no || "").toLowerCase().includes(term) ||
      (r.account_number || "").toLowerCase().includes(term) ||
      (r.entry_type || "").toLowerCase().includes(term) ||
      (r.status || "").toLowerCase().includes(term) ||
      (r.product || "").toLowerCase().includes(term) ||
      String(r.amount || "").includes(term)
    );
  }, [reportData.data, searchTerm]);

  // ─────────────────────────────────────────────────────────────
  // Fetch Payout Ledger Report
  // ─────────────────────────────────────────────────────────────
  const fetchLedger = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      setErrorState(null);

      try {
        const queryParams = new URLSearchParams();
        if (targetUserId.trim()) {
          queryParams.append("user_id", targetUserId.trim());
        }
        if (walletType && walletType !== "all") {
          queryParams.append("wallet_type", walletType);
        }
        if (fromDate) {
          queryParams.append("from_date", fromDate);
        }
        if (toDate) {
          queryParams.append("to_date", toDate);
        }
        queryParams.append("page", String(page));
        queryParams.append("page_size", String(pageSize));

        const data = await fetchWrapper(
          `${BASE_URL}/payout_ledger_report?${queryParams.toString()}`,
          { method: "GET" }
        );

        if (data && data.status !== false) {
          setReportData({
            user_id: data.user_id || targetUserId,
            user_name: data.user_name || "API Partner",
            user_phone: data.user_phone || "",
            role_id: data.role_id || roleId,
            current_main_wallet: parseFloat(data.current_main_wallet) || 0,
            current_recharge_wallet: parseFloat(data.current_recharge_wallet) || 0,
            period: {
              from: data.period?.from || fromDate,
              to: data.period?.to || toDate,
              opening_balance: parseFloat(data.period?.opening_balance) || 0,
              closing_balance: parseFloat(data.period?.closing_balance) || 0,
            },
            summary: {
              total_fund_in: parseFloat(data.summary?.total_fund_in) || 0,
              total_payout: parseFloat(data.summary?.total_payout) || 0,
              total_charge: parseFloat(data.summary?.total_charge) || 0,
              net_movement: parseFloat(data.summary?.net_movement) || 0,
              total_entries: parseInt(data.summary?.total_entries, 10) || (data.data?.length || 0),
              success_count: parseInt(data.summary?.success_count, 10) || 0,
              pending_count: parseInt(data.summary?.pending_count, 10) || 0,
              failed_count: parseInt(data.summary?.failed_count, 10) || 0,
            },
            page: parseInt(data.page, 10) || page,
            page_size: parseInt(data.page_size, 10) || pageSize,
            total: parseInt(data.total, 10) || (data.data?.length || 0),
            total_pages: parseInt(data.total_pages, 10) || 1,
            data: Array.isArray(data.data) ? data.data : [],
          });
          setLastSyncTime(new Date().toLocaleTimeString());
        } else {
          setErrorState({
            message: data?.message || "Failed to load payout ledger report.",
            is403: (data?.message || "").toLowerCase().includes("role_id") || (data?.message || "").toLowerCase().includes("downline"),
          });
        }
      } catch (err) {
        console.error("fetchLedger error:", err);
        const errMsg = err.message || "Network error loading ledger report.";
        setErrorState({
          message: errMsg,
          is403: err.status === 403 || errMsg.toLowerCase().includes("downline") || errMsg.toLowerCase().includes("role_id"),
        });
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [targetUserId, walletType, fromDate, toDate, page, pageSize, roleId]
  );

  // Fetch on mount and when query dependencies change
  useEffect(() => {
    fetchLedger(false);
  }, [fetchLedger]);

  // ─────────────────────────────────────────────────────────────
  // Interaction Handlers (Rule: any filter change resets page to 1)
  // ─────────────────────────────────────────────────────────────
  const handleWalletTypeChange = (e) => {
    setWalletType(e.target.value);
    setPage(1);
  };

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
    setPage(1);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
    setPage(1);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  const handleResetFilters = () => {
    setWalletType("all");
    setFromDate(getMonthStartStr());
    setToDate(getTodayStr());
    setPageSize(25);
    setPage(1);
    if (!isAdmin) setTargetUserId(String(currentUserId));
    else setTargetUserId("");
  };

  const handleQuickPreset = (preset) => {
    const today = getTodayStr();
    if (preset === "today") {
      setFromDate(today);
      setToDate(today);
    } else if (preset === "last7") {
      setFromDate(getLast7DaysStr());
      setToDate(today);
    } else if (preset === "this_month") {
      setFromDate(getMonthStartStr());
      setToDate(today);
    }
    setPage(1);
  };

  const handleHardRefresh = async () => {
    setIsHardRefreshing(true);
    try {
      await fetchLedger(false);
      ShowToast("Ledger statement hard-refreshed from server!", "success");
    } finally {
      setIsHardRefreshing(false);
    }
  };

  const handleCopy = (text, label = "Reference") => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    ShowToast(`${label} copied to clipboard!`, "success");
  };

  const toggleRowExpansion = (rowId) => {
    setExpandedRowId((prev) => (prev === rowId ? null : rowId));
  };

  // ─────────────────────────────────────────────────────────────
  // Status Badge Mapper (Fixed color mapping)
  // ─────────────────────────────────────────────────────────────
  const renderStatusBadge = (statusStr) => {
    const s = (statusStr || "").toLowerCase().trim();
    if (s === "success" || s === "accepted" || s === "successful") {
      return <span className="plr-status-badge plr-badge-success">● Success</span>;
    }
    if (s === "pending" || s === "processing" || s === "in_process") {
      return <span className="plr-status-badge plr-badge-pending">⏳ Pending</span>;
    }
    return <span className="plr-status-badge plr-badge-failed">✕ Failed</span>;
  };

  const handleExportStatement = (format) => {
    const cols = [
      { key: "date_time", label: "Date & Time" },
      { key: "type", label: "Type" },
      { key: "product", label: "Product" },
      { key: "reference_no", label: "Reference No" },
      { key: "ack_no", label: "Ack No / Bank RRN" },
      { key: "beneficiary", label: "Beneficiary" },
      { key: "account_number", label: "Account No" },
      { key: "credit", label: "Credit (+)" },
      { key: "debit", label: "Debit (-)" },
      { key: "charge", label: "Charge" },
      { key: "status", label: "Status" },
      { key: "running_balance", label: "Running Balance" },
    ];
    exportDataToFile(
      reportData.statement || [],
      `Payout_Ledger_${fromDate}_to_${toDate}`,
      format,
      cols
    );
  };

  return (
    <div className="plr-wrapper">
      {/* ── Top Header Bar ── */}
      <div className="plr-header-card">
        <div className="plr-header-left">
          <div className="plr-header-icon-box">
            <span>📑</span>
          </div>
          <div>
            <div className="plr-breadcrumb">
              <span>Reports &amp; Analytics</span> › <span>Product Wise Report</span> ›{" "}
              <span className="plr-active-crumb">Payout Ledger Report</span>
            </div>
            <h1 className="plr-header-title">
              Payout Ledger Statement
              {reportData.user_name && (
                <span className="plr-user-title-tag">
                  👤 {reportData.user_name} ({reportData.user_phone || `UID: ${reportData.user_id}`})
                </span>
              )}
            </h1>
            <p className="plr-header-sub">
              Single unified chronological statement combining Bank Fund-In credits and Wallet Settlements / Payout debits with opening and closing reconciliation.
            </p>
          </div>
        </div>

        <div className="plr-header-actions">
          <button
            type="button"
            className="plr-btn-header-export plr-btn-header-excel"
            onClick={() => handleExportStatement("excel")}
            title="Export full ledger statement to Excel (.xlsx)"
            disabled={loading || reportData.data.length === 0}
          >
            <FaFileExcel className="me-1" />
            <span>Excel</span>
          </button>
          <button
            type="button"
            className="plr-btn-header-export plr-btn-header-csv"
            onClick={() => handleExportStatement("csv")}
            title="Export full ledger statement to CSV (.csv)"
            disabled={loading || reportData.data.length === 0}
          >
            <FaFileCsv className="me-1" />
            <span>CSV</span>
          </button>
          <button
            type="button"
            className="plr-btn-refresh"
            onClick={handleHardRefresh}
            disabled={loading || isHardRefreshing}
            title={`Last synced at ${lastSyncTime}. Click for hard refresh from server.`}
          >
            <span className={loading || isHardRefreshing ? "plr-spin" : ""}>🔄</span>
            <span>{isHardRefreshing ? "Refreshing..." : "Hard Refresh"}</span>
            <span className="plr-sync-time">{lastSyncTime}</span>
          </button>
        </div>
      </div>

      {/* ── Filter Bar ── */}
      <div className="plr-filter-panel">
        <div className="plr-filter-grid">
          {/* Admin User ID Selector */}
          {isAdmin && (
            <div className="plr-form-group">
              <label className="plr-form-label">API Partner / User ID</label>
              <input
                type="text"
                className="plr-form-control plr-mono-inp"
                placeholder="User ID (e.g. 452)"
                value={targetUserId}
                onChange={(e) => {
                  setTargetUserId(e.target.value.replace(/\D/g, ""));
                  setPage(1);
                }}
              />
            </div>
          )}

          {/* Wallet Type */}
          <div className="plr-form-group">
            <label className="plr-form-label">Wallet Type</label>
            <select
              className="plr-form-control"
              value={walletType}
              onChange={handleWalletTypeChange}
            >
              <option value="all">All Wallets</option>
              <option value="main_wallet">💼 Main Wallet</option>
              <option value="recharge_wallet">⚡ Recharge Wallet</option>
            </select>
          </div>

          {/* From Date */}
          <div className="plr-form-group">
            <label className="plr-form-label">From Date (IST)</label>
            <input
              type="date"
              className="plr-form-control"
              value={fromDate}
              onChange={handleFromDateChange}
              max={toDate || getTodayStr()}
            />
          </div>

          {/* To Date */}
          <div className="plr-form-group">
            <label className="plr-form-label">To Date (IST)</label>
            <input
              type="date"
              className="plr-form-control"
              value={toDate}
              onChange={handleToDateChange}
              min={fromDate}
              max={getTodayStr()}
            />
          </div>

          {/* Rows Per Page */}
          <div className="plr-form-group plr-form-group-sm">
            <label className="plr-form-label">Page Size</label>
            <select
              className="plr-form-control"
              value={pageSize}
              onChange={handlePageSizeChange}
            >
              <option value={10}>10 rows</option>
              <option value={25}>25 rows</option>
              <option value={50}>50 rows</option>
              <option value={100}>100 rows</option>
              <option value={200}>200 rows</option>
            </select>
          </div>

          {/* Quick Presets & Reset */}
          <div className="plr-filter-btn-group">
            <button
              type="button"
              className="plr-btn-chip"
              onClick={() => handleQuickPreset("today")}
            >
              Today
            </button>
            <button
              type="button"
              className="plr-btn-chip"
              onClick={() => handleQuickPreset("last7")}
            >
              Last 7 Days
            </button>
            <button
              type="button"
              className="plr-btn-chip"
              onClick={() => handleQuickPreset("this_month")}
            >
              This Month
            </button>
            <button
              type="button"
              className="plr-btn-reset"
              onClick={handleResetFilters}
              title="Reset all filters"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* ── Error Banner State ── */}
      {errorState && (
        <div className={`plr-error-banner ${errorState.is403 ? "plr-error-boundary" : ""}`}>
          <div className="plr-error-content">
            <span className="plr-error-icon">⚠️</span>
            <div>
              <strong>{errorState.is403 ? "Access Permission Boundary" : "Statement Query Notice"}</strong>
              <p>{errorState.message}</p>
            </div>
          </div>
          <button
            type="button"
            className="plr-btn-retry"
            onClick={() => fetchLedger(false)}
          >
            Retry Query
          </button>
        </div>
      )}

      {/* ── Statement Balance Flow Banner (First Thing an Accountant Looks For) ── */}
      <div className="plr-balance-flow-card">
        <div className="plr-flow-item">
          <span className="plr-flow-lbl">Opening Balance</span>
          <div className="plr-flow-val">{formatINR(reportData.period.opening_balance)}</div>
          <span className="plr-flow-date">Period Start: {reportData.period.from || fromDate}</span>
        </div>

        <div className="plr-flow-arrow">
          <div className="plr-arrow-line"></div>
          <div className="plr-arrow-head">➔</div>
        </div>

        <div className="plr-flow-item">
          <span className="plr-flow-lbl">Closing Balance</span>
          <div className="plr-flow-val text-primary">{formatINR(reportData.period.closing_balance)}</div>
          <span className="plr-flow-date">Period End: {reportData.period.to || toDate}</span>
        </div>

        <div className="plr-flow-divider"></div>

        <div className="plr-flow-live-chips">
          <div className="plr-live-chip">
            <span className="plr-live-icon">💼</span>
            <div>
              <span className="plr-live-lbl">Live Main Wallet</span>
              <strong className="plr-live-amt">{formatINR(reportData.current_main_wallet)}</strong>
            </div>
          </div>
            {/* <div className="plr-live-chip">
              <span className="plr-live-icon">⚡</span>
              <div>
                <span className="plr-live-lbl">Live Recharge Wallet</span>
                <strong className="plr-live-amt">{formatINR(reportData.current_recharge_wallet)}</strong>
              </div>
            </div> */}
        </div>
      </div>

      {/* ── Summary Cards Grid (Aggregated Independent of Pagination) ── */}
      <div className="plr-summary-grid">
        {/* Row 1: Financial Movement Totals */}
        <div className="plr-metric-box plr-box-green">
          <div className="plr-metric-top">
            <span className="plr-metric-label">Total Fund In (Deposits)</span>
            <span className="plr-metric-icon">💵</span>
          </div>
          <div className="plr-metric-val text-success">
            {formatINR(reportData.summary.total_fund_in, true, "credit")}
          </div>
          <span className="plr-metric-sub">Bank deposits credited</span>
        </div>

        <div className="plr-metric-box plr-box-red">
          <div className="plr-metric-top">
            <span className="plr-metric-label">Total Payouts (Settlements)</span>
            <span className="plr-metric-icon">📤</span>
          </div>
          <div className="plr-metric-val text-danger">
            {formatINR(reportData.summary.total_payout, true, "debit")}
          </div>
          <span className="plr-metric-sub">Paid out to beneficiaries</span>
        </div>

        <div className="plr-metric-box plr-box-amber">
          <div className="plr-metric-top">
            <span className="plr-metric-label">Total Platform Charges</span>
            <span className="plr-metric-icon">🏷️</span>
          </div>
          <div className="plr-metric-val text-warning">
            {formatINR(reportData.summary.total_charge)}
          </div>
          <span className="plr-metric-sub">Payout transaction fees</span>
        </div>

        <div className="plr-metric-box plr-box-blue">
          <div className="plr-metric-top">
            <span className="plr-metric-label">Net Movement</span>
            <span className="plr-metric-icon" title="Net Movement = Total Fund In − Total Payout − Total Charges">ℹ️</span>
          </div>
          <div className="plr-metric-val text-indigo">
            {formatINR(reportData.summary.net_movement, true)}
          </div>
          <span className="plr-metric-sub">Fund In − Payout − Charges</span>
        </div>
      </div>

      {/* ── Volume & Status Counters ── */}
      <div className="plr-counters-bar">
        <div className="plr-counter-item">
          <span className="plr-counter-dot bg-success"></span>
          <span className="plr-counter-lbl">Successful Txns:</span>
          <strong>{reportData.summary.success_count}</strong>
        </div>
        <div className="plr-counter-item">
          <span className="plr-counter-dot bg-warning"></span>
          <span className="plr-counter-lbl">Pending Txns:</span>
          <strong>{reportData.summary.pending_count}</strong>
        </div>
        <div className="plr-counter-item">
          <span className="plr-counter-dot bg-danger"></span>
          <span className="plr-counter-lbl">Failed Txns:</span>
          <strong>{reportData.summary.failed_count}</strong>
        </div>
        <div className="plr-counter-item">
          <span className="plr-counter-dot bg-primary"></span>
          <span className="plr-counter-lbl">Total Period Entries:</span>
          <strong>{reportData.summary.total_entries}</strong>
        </div>
      </div>

      {/* ── Data Reconciliation Guidance Disclosure ── */}
      <div className="plr-guidance-callout">
        <span className="plr-guidance-icon">💡</span>
        <div className="plr-guidance-text">
          <strong>Reconciliation Note:</strong> Net Movement (Fund In − Payout − Charge) equals the delta between Closing Balance and Opening Balance when no other products (such as BBPS, Mobile Recharge, or AEPS) moved the wallet balance in this time window. Any non-payout transaction is disclosed on its respective Main Wallet row as <em>Balance Drift</em>.
        </div>
      </div>

      {/* ── Ledger Data Table ── */}
      <div className="plr-table-card">
        {/* Table Toolbar Header with Title, Search, Export Controls in a Single Line */}
        <div className="plr-table-card-header">
          <div className="plr-table-card-header-left">
            <h3 className="plr-table-title">Payout Ledger</h3>
            <span className="plr-badge plr-badge-records">
              {displayedRows.length} {displayedRows.length !== reportData.data.length ? `of ${reportData.data.length}` : "records"}
            </span>
          </div>

          <div className="plr-table-card-header-right">
            {/* Search Input in Single Line */}
            <div className="position-relative">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search ledger..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: "30px", width: "170px", height: "34px", borderRadius: "6px" }}
              />
              <i className="iconoir-search position-absolute text-muted" style={{ left: "9px", top: "50%", transform: "translateY(-50%)", fontSize: "14px" }}></i>
            </div>

            {/* Export Buttons in Single Line */}
            <div className="btn-group shadow-sm" role="group" aria-label="Export Ledger">
              <button
                type="button"
                className="btn btn-outline-success btn-sm d-inline-flex align-items-center gap-1.5 px-2.5 text-nowrap"
                onClick={() => handleExportStatement("excel")}
                disabled={loading || reportData.data.length === 0}
                title="Export complete statement to Excel (.xlsx)"
                style={{ height: "34px", fontWeight: 600, borderTopLeftRadius: "6px", borderBottomLeftRadius: "6px" }}
              >
                <FaFileExcel className="fs-6" />
                <span>Excel</span>
              </button>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1.5 px-2.5 text-nowrap"
                onClick={() => handleExportStatement("csv")}
                disabled={loading || reportData.data.length === 0}
                title="Export complete statement to CSV (.csv)"
                style={{ height: "34px", fontWeight: 600, borderTopRightRadius: "6px", borderBottomRightRadius: "6px" }}
              >
                <FaFileCsv className="fs-6" />
                <span>CSV</span>
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="plr-table-skeleton-wrap">
            <div className="plr-skeleton-row"></div>
            <div className="plr-skeleton-row"></div>
            <div className="plr-skeleton-row"></div>
            <div className="plr-skeleton-row"></div>
            <div className="plr-skeleton-row"></div>
          </div>
        ) : displayedRows.length === 0 ? (
          <div className="plr-empty-state">
            <span className="plr-empty-icon">📭</span>
            <h3>{reportData.data.length > 0 ? "No Matching Entries Found" : "No Ledger Entries Found"}</h3>
            <p>
              {reportData.data.length > 0
                ? `No transactions match your search "${searchTerm}".`
                : `No fund-in or payout settlement transactions were recorded for the selected wallet and date range (${fromDate} to ${toDate}).`}
            </p>
          </div>
        ) : (
          <div className="table-responsive plr-table-wrap">
            <table className="table plr-table align-middle">
              <thead>
                <tr>
                  <th>Date &amp; Time</th>
                  <th>Entry Type</th>
                  <th>Wallet</th>
                  <th>Ref ID</th>
                  <th>Beneficiary / Source</th>
                  <th>Amount</th>
                  <th>Charge</th>
                  <th>Opening Bal</th>
                  <th>Closing Bal</th>
                  <th>Status</th>
                  <th className="text-end">Details</th>
                </tr>
              </thead>
              <tbody>
                {displayedRows.map((row) => {
                  const isExpanded = expandedRowId === row.id;
                  const isCredit = row.direction === "credit";
                  const isFundIn = row.entry_type === "fund_in" || row.product === "wallet_load";
                  const isMainWallet = !row.wallet_type || (row.wallet_type || "").toLowerCase().includes("main");
                  const hasDrift = isMainWallet && row.balance_drift_since_previous_row !== null && row.balance_drift_since_previous_row !== undefined;

                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        className={`plr-tr-row ${isExpanded ? "plr-tr-expanded" : ""} ${hasDrift ? "plr-tr-drift-marker" : ""}`}
                        onClick={() => toggleRowExpansion(row.id)}
                      >
                        {/* Date & Time */}
                        <td className="plr-td-date">
                          <span>{row.date || "N/A"}</span>
                        </td>

                        {/* Entry Type */}
                        <td>
                          {isFundIn ? (
                            <span className="plr-entry-badge plr-badge-fundin">⚡ Fund In</span>
                          ) : (
                            <span className="plr-entry-badge plr-badge-payout">💼 Payout</span>
                          )}
                        </td>

                        {/* Wallet */}
                        <td>
                          <span className="plr-wallet-chip">{row.wallet_type || "Main Wallet"}</span>
                        </td>

                        {/* Reference Number */}
                        <td>
                          <div className="plr-ref-wrap" onClick={(e) => e.stopPropagation()}>
                            <span className="plr-mono-text">{row.internal_ref || `#${row.id}`}</span>
                            <button
                              type="button"
                              className="plr-btn-copy"
                              onClick={() => handleCopy(row.internal_ref || String(row.id), "Ref ID")}
                              title="Copy Reference"
                            >
                              📋
                            </button>
                          </div>
                        </td>

                        {/* Beneficiary / Bank */}
                        <td>
                          <div className="plr-beneficiary-cell">
                            <strong className="plr-bene-title">
                              {row.beneficiary || row.bank_name || (isFundIn ? "Bank Deposit Credit" : "Beneficiary Payout")}
                            </strong>
                            {row.r_type && <span className="plr-bene-sub">{row.r_type}</span>}
                          </div>
                        </td>

                        {/* Transaction Amount */}
                        <td>
                          <strong className={`plr-amt-val ${isCredit ? "text-success" : "text-danger"}`}>
                            {formatINR(row.amount, true, row.direction)}
                          </strong>
                        </td>

                        {/* Charge */}
                        <td>
                          <span className="plr-charge-val">
                            {parseFloat(row.charge) > 0 ? formatINR(row.charge) : "—"}
                          </span>
                        </td>

                        {/* Opening Balance */}
                        <td>
                          <div className="plr-bal-cell">
                            <span>{formatINR(row.opening_balance)}</span>
                            {hasDrift && (
                              <span
                                className="plr-drift-tag"
                                title={`Interim non-payout balance drift of ₹${row.balance_drift_since_previous_row}`}
                              >
                                ⚠️ Drift
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Closing Balance */}
                        <td>
                          <strong className="plr-closing-val">{formatINR(row.closing_balance)}</strong>
                        </td>

                        {/* Status */}
                        <td>{renderStatusBadge(row.status)}</td>

                        {/* Details Toggle Button */}
                        <td className="text-end" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className={`plr-btn-expand ${isExpanded ? "plr-btn-expanded" : ""}`}
                            onClick={() => toggleRowExpansion(row.id)}
                            aria-expanded={isExpanded}
                            title="Toggle transaction detail"
                          >
                            {isExpanded ? "▲ Hide" : "▼ Details"}
                          </button>
                        </td>
                      </tr>

                      {/* ── Inline Row Detail Accordion (In-place Toggle) ── */}
                      {isExpanded && (
                        <tr className="plr-expand-container-tr">
                          <td colSpan={11} className="plr-expand-container-td">
                            <div className="plr-expand-box">
                              <div className="plr-expand-grid">
                                <div className="plr-detail-item">
                                  <span className="plr-detail-lbl">Internal Transaction ID</span>
                                  <div className="plr-detail-val-copy">
                                    <span className="plr-mono-bold">{row.internal_txn_id || "N/A"}</span>
                                    {row.internal_txn_id && (
                                      <button
                                        type="button"
                                        className="plr-btn-copy"
                                        onClick={() => handleCopy(row.internal_txn_id, "Internal Txn ID")}
                                      >
                                        📋 Copy
                                      </button>
                                    )}
                                  </div>
                                </div>

                                <div className="plr-detail-item">
                                  <span className="plr-detail-lbl">Provider Ref / Bank RRN</span>
                                  <div className="plr-detail-val-copy">
                                    <span className="plr-mono-bold">{row.provider_ref || "N/A"}</span>
                                    {row.provider_ref && (
                                      <button
                                        type="button"
                                        className="plr-btn-copy"
                                        onClick={() => handleCopy(row.provider_ref, "Provider Ref / RRN")}
                                      >
                                        📋 Copy
                                      </button>
                                    )}
                                  </div>
                                </div>

                                <div className="plr-detail-item">
                                  <span className="plr-detail-lbl">Product &amp; Route</span>
                                  <span className="plr-detail-val">
                                    {row.product || "wallet_settlement"} {row.r_type ? `(${row.r_type})` : ""}
                                  </span>
                                </div>

                                <div className="plr-detail-item">
                                  <span className="plr-detail-lbl">Transaction Status / Message</span>
                                  <span className="plr-detail-val">{row.description || "Processed successfully"}</span>
                                </div>
                              </div>

                              {/* Balance Drift Disclosure Box */}
                              {hasDrift && (
                                <div className="plr-drift-alert-box">
                                  <span className="plr-drift-icon">⚠️</span>
                                  <div>
                                    <strong>Main Wallet Balance Drift Disclosure:</strong>
                                    <p>
                                      A non-payout transaction of{" "}
                                      <strong>{formatINR(row.balance_drift_since_previous_row)}</strong> affected the
                                      main wallet balance between this ledger entry and the prior row (e.g. BBPS, DMT, or Recharge service).
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Server-Side Pagination Bar ── */}
        <div className="plr-pagination-bar">
          <div className="plr-page-info">
            Showing page <strong>{reportData.page}</strong> of <strong>{reportData.total_pages}</strong> (
            {reportData.total} total transactions)
          </div>

          <div className="plr-page-controls">
            <button
              type="button"
              className="plr-page-btn"
              disabled={reportData.page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ◀ Previous
            </button>

            <span className="plr-page-current">Page {reportData.page}</span>

            <button
              type="button"
              className="plr-page-btn"
              disabled={reportData.page >= reportData.total_pages || loading}
              onClick={() => setPage((p) => Math.min(reportData.total_pages, p + 1))}
            >
              Next ▶
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
